import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Activity } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Marquee } from "@/components/Marquee";
import { AdSlot } from "@/components/AdSlot";
import { Reveal, MaskedLines, LiveBeats } from "@/components/Reveal";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { ACCORDION_ITEMS } from "@/components/Facts";
import { ICONS, CALCULATORS } from "@/data/calculators";
import { smart, decimal, full } from "@/lib/format";
import { Seo } from "@/components/Seo";

const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Curiosímetro",
  inLanguage: "es",
  description:
    "Seis calculadoras insólitas del cuerpo humano y la vida cotidiana: latidos, sueño, domingos, pantallas, cabello y agua.",
};

const MARQUEE_ITEMS = [
  "7.200 litros de sangre al día",
  "4.160 domingos en una vida",
  "25 años durmiendo hasta los 75",
  "96 desbloqueos de móvil al día",
  "100.000 folículos trabajando ahora mismo",
  "≈3.000 millones de latidos hasta los 80",
  "≈29.000 litros de agua en una vida",
];

function EcgLine() {
  return (
    <svg viewBox="0 0 220 48" className="h-12 w-full" fill="none">
      <path
        d="M0 24 H30 L40 24 L48 10 L58 38 L66 24 H100 L110 24 L118 10 L128 38 L136 24 H170 L180 24 L188 10 L198 38 L206 24 H220"
        stroke="var(--primary)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: "drop-shadow(0 0 6px rgba(217,56,30,0.35))" }}
      />
      <motion.circle
        r="3.5"
        fill="var(--primary)"
        animate={{ cx: [0, 55, 110, 165, 220], cy: [24, 24, 24, 24, 24] }}
        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
      />
    </svg>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, -2.5]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 pb-20 pt-14 sm:px-8 lg:grid-cols-12 lg:items-center lg:gap-8 lg:pb-28 lg:pt-24">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary"
          >
            [ Mediciones de la existencia — Ed. Curiosímetro ]
          </motion.p>
          <MaskedLines
            as="h1"
            lines={["La contabilidad", "secreta de", "tu existencia."]}
            className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
            delay={0.25}
          />
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-md text-lg font-light leading-relaxed text-muted-foreground"
          >
            Seis calculadoras insólitas sobre datos curiosos de tu vida.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#calculadoras"
              data-testid="hero-start-button"
              className="group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-all hover:shadow-offset-accent"
            >
              Empezar a medir
              <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </a>
            <a
              href="#sabias-que"
              data-testid="hero-facts-link"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium transition-colors hover:border-primary hover:text-primary"
            >
              ¿Sabías que…?
            </a>
          </motion.div>
        </div>

        <motion.div style={{ y, rotate }} className="relative lg:col-span-5" data-testid="hero-parallax-card">
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: 1.5 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ delay: 0.55, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-lg border border-border bg-card p-7 shadow-offset"
          >
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">Monitor vital · en directo</p>
              <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-primary">
                <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-primary" /> Live
              </span>
            </div>
            <EcgLine />
            <div className="mt-4 flex items-end justify-between gap-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">Latidos desde que abriste esta página</p>
                <p className="mt-1 text-4xl font-bold tracking-tighter text-primary" data-testid="hero-live-beats">
                  <LiveBeats bpm={70} />
                </p>
              </div>
              <Activity className="mb-1 h-8 w-8 text-primary/30" />
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -left-6 top-1/3 hidden rounded-md border border-border bg-card px-4 py-3 shadow-offset sm:block"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">Sangre al día</p>
            <p className="font-serif text-lg">7.200 litros</p>
          </motion.div>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute -bottom-6 -right-3 hidden rounded-md border border-border bg-card px-4 py-3 shadow-offset sm:block"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">Domingos en una vida</p>
            <p className="font-serif text-lg">4.160</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function BentoCard({ calc, index }) {
  const Icon = ICONS[calc.icon];
  const preview = useMemo(() => calc.compute(calc.defaults()), [calc]);
  const wide = calc.bentoSpan.includes("col-span-12");

  return (
    <Reveal className={calc.bentoSpan} delay={index * 0.06}>
      <Link
        to={`/${calc.slug}`}
        data-testid={`calculator-card-${calc.slug}`}
        className={`group flex h-full border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-offset ${wide ? "flex-col justify-between gap-8 md:flex-row md:items-center" : "flex-col"} p-7 sm:p-8`}
        style={{ borderTopColor: calc.accent, borderTopWidth: 3 }}
      >
        <div className={wide ? "max-w-xl" : ""}>
          <div className="flex items-center justify-between">
            <span className="flex h-10 w-10 items-center justify-center rounded-md" style={{ backgroundColor: `${calc.accent}14`, color: calc.accent }}>
              <Icon className="h-5 w-5" />
            </span>
            <span className="font-mono text-xs text-ink-muted">/{String(index + 1).padStart(2, "0")}</span>
          </div>
          <h3 className="mt-5 font-serif text-2xl font-medium tracking-tight">{calc.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{calc.subtitle}</p>
        </div>

        <div className={wide ? "flex flex-col items-start gap-6 md:items-end" : "mt-8"}>
          <div className={wide ? "md:text-right" : ""}>
            <p className="text-3xl font-bold tracking-tighter sm:text-4xl" style={{ color: calc.accent }}>
              {calc.slug === "latidos" && smart(preview.big.value)}
              {calc.slug === "sueno" && decimal(preview.big.value, 1)}
              {calc.slug === "domingos" && full(preview.big.value)}
              {calc.slug === "pantallas" && decimal(preview.big.value, 1)}
              {calc.slug === "cabello-unas" && decimal(preview.big.value, 0)}
              {calc.slug === "agua" && smart(preview.big.value)}
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">{preview.big.label}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium transition-colors" style={{ color: calc.accent }}>
            Calcular ahora
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

function Bento() {
  return (
    <section id="calculadoras" className="mx-auto max-w-7xl scroll-mt-24 px-5 py-20 sm:px-8 lg:py-28" data-testid="calculators-section">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">Las calculadoras</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h2 className="max-w-xl font-serif text-3xl font-medium tracking-tight sm:text-4xl">
            Seis formas de medirte que no aparecen en ningún manual
          </h2>
          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
            Elige una, mueve los controles y mira cómo tu vida se traduce a números redondos.
          </p>
        </div>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-12">
        {CALCULATORS.map((c, i) => (
          <BentoCard key={c.slug} calc={c} index={i} />
        ))}
      </div>
    </section>
  );
}

function Facts() {
  return (
    <section id="sabias-que" className="scroll-mt-24 border-y border-border bg-secondary/40" data-testid="facts-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:py-28">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">Sabías que</p>
          <h2 className="mt-3 font-serif text-3xl font-medium tracking-tight sm:text-4xl">
            Tu cuerpo es un contable incansable
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground">
            Cada segundo está pasando factura y produciendo cifras: latidos, milímetros
            de cabello, desbloqueos de móvil. Casi nunca las sumamos. Aquí sí.
          </p>
          <div className="mt-8">
            <Accordion type="single" collapsible className="w-full">
              {ACCORDION_ITEMS.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-border">
                  <AccordionTrigger className="text-left font-serif text-lg hover:no-underline" data-testid={`myth-trigger-${i}`}>
                    {item.myth}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                    {item.reality}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <figure className="relative">
            <div className="overflow-hidden rounded-md border border-border shadow-offset">
              <img
                src="https://images.unsplash.com/photo-1617020329399-a1fe5d8646d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA6MDV8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwZWRpdG9yaWFsJTIwcG9ydHJhaXQlMjBwYXBlciUyMHRleHR1cmUlMjBza2luJTIwZGV0YWlscyUyMGFic3RyYWN0JTIwZ2VvbWV0cnl8ZW58MHx8fHdoaXRlfDE3OTE3OTk0NzF8MA&ixlib=rb-4.1.0&q=85"
                alt="Textura editorial de piel y papel, detalle abstracto"
                className="h-[420px] w-full object-cover lg:h-[520px]"
                loading="lazy"
              />
            </div>
            <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
              Fig. 01 — El cuerpo como instrumento de medida
            </figcaption>
            <motion.div
              animate={{ y: [0, -7, 0] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -left-3 bottom-16 rounded-md border border-border bg-card px-4 py-3 shadow-offset sm:-left-6"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted">Latidos hasta los 80</p>
              <p className="font-serif text-lg text-primary">≈ 3.000 millones</p>
            </motion.div>
          </figure>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Curiosímetro — Calculadoras insólitas del cuerpo humano"
        description="Seis calculadoras insólitas: latidos de tu vida, agua bebida, años soñando, domingos restantes, tiempo en pantallas y metros de cabello. Descubre tus cifras."
        jsonLd={WEBSITE_JSONLD}
      />
      <Nav />
      <main>
        <Hero />
        <Marquee items={MARQUEE_ITEMS} />
        <Bento />
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <AdSlot slot="6824110255" label="Publicidad" minHeight={220} />
        </div>
        <Facts />
      </main>
      <Footer />
    </>
  );
}
